import { Injectable, Injector } from '@angular/core';
import { CreateFormOptions, FieldConfig, FormConfig, FormControlsFor, FormValidationResult, IFormBuilder, TypedFormGroup } from './core-types';
import { FormControl, FormGroup, FormArray, ValidatorFn, Validators } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class FormBuilderService implements IFormBuilder<any> {
  private readonly formCache = new Map<string, TypedFormGroup<any>>();
  private readonly configCache = new Map<string, FormConfig<any>>();

  constructor(private readonly injector: Injector) { }

  // ============================================================
  // 1. CREAR FORMULARIOS
  // ============================================================

  /**
   * Crear un formulario tipado a partir de una configuración
   * 
   * @example
   * const form = this.formBuilder.create<TyreProduct>(
   *   tyreFormConfig,
   *   { initialValue: { width: 225 } }
   * );
   */
  create<T extends Record<string, any>>(
    config: FormConfig<T>,
    options: CreateFormOptions<T> = {}
  ): TypedFormGroup<T> {
    // Crear controles para cada campo
    const controls = this.createControls<T>(config, options.initialValue);

    // Crear el FormGroup
    const formGroup = new FormGroup(
      controls as any,
      {
        validators: config.formValidators,
        asyncValidators: config.formAsyncValidators,
      }
    );

    // Crear el TypedFormGroup
    // Establecer el prototipo de FormGroup para que herede todos los métodos de TypedFormGroup
    Object.setPrototypeOf(formGroup, TypedFormGroup.prototype);
    const typedForm = formGroup as TypedFormGroup<T>;

    typedForm.controls = controls;
    typedForm._config = config;

    // ✅ APLICAR REGLAS CONDICIONALES AUTOMÁTICAMENTE
    if (config.conditionalRules && config.conditionalRules.length > 0) {
      typedForm.applyConditionalRules(config.conditionalRules);
    }

    // Cachear si tiene nombre
    if (config.name) {
      this.formCache.set(config.name, typedForm);
      this.configCache.set(config.name, config);
    }

    return typedForm as TypedFormGroup<T>;
  }

  /**
   * Crear un formulario usando un tipo de configuración registrado
   * 
   * @example
   * this.registerFormConfig('tyreProduct', tyreFormConfig);
   * const form = this.createByName<TyreProduct>('tyreProduct');
   */
  createByName<T extends Record<string, any>>(
    name: string,
    options: CreateFormOptions<T> = {}
  ): TypedFormGroup<T> {
    const config = this.configCache.get(name);
    if (!config) {
      throw new Error(`FormConfig "${name}" no está registrada`);
    }
    return this.create<T>(config as FormConfig<T>, options);
  }

  /**
   * Registrar una configuración de formulario para reutilización
   */
  registerFormConfig<T extends Record<string, any>>(
    name: string,
    config: FormConfig<T>
  ): void {
    this.configCache.set(name, config);
  }

  /**
   * Obtener una configuración registrada
   */
  getFormConfig<T extends Record<string, any>>(
    name: string
  ): FormConfig<T> | undefined {
    return this.configCache.get(name) as FormConfig<T> | undefined;
  }

  // ============================================================
  // 2. CREAR CONTROLES
  // ============================================================

  /**
   * Crear los FormControls basados en la configuración
   */
  private createControls<T extends Record<string, any>>(
    config: FormConfig<T>,
    initialValue?: Partial<T>
  ): FormControlsFor<T> {
    const controls: any = {};

    Object.keys(config.fields).forEach((fieldName) => {
      const key = fieldName as keyof T;
      const fieldConfig = config.fields[key];

      controls[fieldName] = this.createFormControl(
        fieldConfig as FieldConfig,
        initialValue?.[key]
      );
    });

    return controls as FormControlsFor<T>;
  }

  /**
   * Crear un FormControl individual con validadores
   */
  private createFormControl<T>(
    fieldConfig: FieldConfig<T>,
    initialValue?: any
  ): any {
    const validators = this.buildValidators(fieldConfig);
    const asyncValidators = fieldConfig.asyncValidators || [];

    // Manejar formularios anidados (grupo)
    if ((fieldConfig as any).type === 'group') {
      const nestedConfig = (fieldConfig as any).nestedFormConfig as FormConfig<any>;
      // usamos create() recursivamente para obtener un TypedFormGroup completo
      return this.create<any>(nestedConfig, { initialValue: initialValue });
    }

    // Manejar arrays de items
    if ((fieldConfig as any).type === 'array') {
      const itemConfig = (fieldConfig as any).itemFormConfig as FormConfig<any>;
      const items: any[] = [];

      // Si hay valores iniciales en forma de array, instanciarlos
      if (Array.isArray(initialValue)) {
        initialValue.forEach((itemVal: any) => {
          const nested = this.create<any>(itemConfig, { initialValue: itemVal });
          items.push(nested);
        });
      }

      const formArray = new FormArray(items as any[], validators);
      return formArray;
    }

    // Por defecto: campo simple (FormControl)
    return new FormControl(
      {
        value: initialValue ?? fieldConfig.initialValue ?? null,
        disabled: fieldConfig.disabled || false,
      },
      {
        validators,
        asyncValidators,
        updateOn: 'change',
      }
    );
  }

  /**
   * Construir validadores a partir de la configuración del campo
   */
  private buildValidators(fieldConfig: FieldConfig): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    // Validador de requerido
    if (fieldConfig.required) {
      validators.push(Validators.required);
    }

    // Validadores específicos
    if (fieldConfig.validators) {
      validators.push(...fieldConfig.validators);
    }

    return validators;
  }

  // ============================================================
  // 3. VALIDACIÓN
  // ============================================================

  /**
   * Validar un formulario completo
   */
  validate<T extends Record<string, any>>(
    form: TypedFormGroup<T>
  ): FormValidationResult<T> {
    return {
      valid: form.valid,
      data: form.getValidValues(),
      formErrors: form.errors || undefined,
    };
  }

  /**
   * Validar un campo específico
   */
  validateField<T extends Record<string, any>, K extends keyof T>(
    form: TypedFormGroup<T>,
    fieldName: K
  ): { valid: boolean; errors?: Record<string, any> } {
    const control = form.getControl(fieldName);
    return {
      valid: control.valid,
      errors: control.errors || undefined,
    };
  }

  /**
   * Obtener todos los errores del formulario
   */
  getAllErrors<T extends Record<string, any>>(
    form: TypedFormGroup<T>
  ): Record<keyof T, Record<string, any>> {
    const errors: any = {};

    Object.keys(form.controls).forEach((fieldName) => {
      const key = fieldName as keyof T;
      const control = form.getControl(key);

      if (control.errors) {
        errors[fieldName] = control.errors;
      }
    });

    return errors;
  }

  // ============================================================
  // 4. TRANSFORMACIÓN DE DATOS
  // ============================================================

  /**
   * Convertir formulario a entidad tipada
   */
  toEntity<T extends Record<string, any>>(
    form: TypedFormGroup<T>
  ): Partial<T> {
    return form.getValidValues();
  }

  /**
   * Convertir entidad a valores del formulario
   */
  toFormValue<T extends Record<string, any>>(
    entity: Partial<T>,
    config: FormConfig<T>
  ): Partial<T> {
    const result: any = {};

    Object.keys(config.fields).forEach((fieldName) => {
      const key = fieldName as keyof T;
      if (key in entity) {
        result[key] = entity[key];
      }
    });

    return result;
  }

  // ============================================================
  // 5. MANIPULACIÓN DE FORMULARIOS
  // ============================================================

  /**
   * Reset del formulario
   */
  reset<T extends Record<string, any>>(
    form: TypedFormGroup<T>,
    value?: Partial<T>
  ): void {
    form.reset(value);
  }

  /**
   * Resetear a valores iniciales
   */
  resetToInitial<T extends Record<string, any>>(
    form: TypedFormGroup<T>,
    config: FormConfig<T>
  ): void {
    const initialValues: any = {};

    Object.keys(config.fields).forEach((fieldName) => {
      const key = fieldName as keyof T;
      const fieldConfig = config.fields[key];
      initialValues[key] = fieldConfig.initialValue ?? null;
    });

    form.setValue(initialValues);
  }

  /**
   * Habilitar todos los controles
   */
  enableAll<T extends Record<string, any>>(form: TypedFormGroup<T>): void {
    Object.keys(form.controls).forEach((fieldName) => {
      const key = fieldName as keyof T;
      form.enableControl(key);
    });
  }

  /**
   * Deshabilitar todos los controles
   */
  disableAll<T extends Record<string, any>>(form: TypedFormGroup<T>): void {
    Object.keys(form.controls).forEach((fieldName) => {
      const key = fieldName as keyof T;
      form.disableControl(key);
    });
  }

  /**
   * Habilitar campos específicos
   */
  enableFields<T extends Record<string, any>>(
    form: TypedFormGroup<T>,
    fields: (keyof T)[]
  ): void {
    fields.forEach((field) => {
      form.enableControl(field);
    });
  }

  /**
   * Deshabilitar campos específicos
   */
  disableFields<T extends Record<string, any>>(
    form: TypedFormGroup<T>,
    fields: (keyof T)[]
  ): void {
    fields.forEach((field) => {
      form.disableControl(field);
    });
  }

  // ============================================================
  // 6. CACHÉ Y GESTIÓN
  // ============================================================

  /**
   * Limpiar el caché de formularios
   */
  clearCache(): void {
    this.formCache.clear();
  }

  /**
   * Obtener un formulario del caché
   */
  getFromCache<T extends Record<string, any>>(
    name: string
  ): TypedFormGroup<T> | undefined {
    return this.formCache.get(name) as TypedFormGroup<T> | undefined;
  }

  /**
   * Eliminar un formulario del caché
   */
  removeFromCache(name: string): void {
    this.formCache.delete(name);
  }

  /**
   * Obtener lista de formularios en caché
   */
  getCachedFormNames(): string[] {
    return Array.from(this.formCache.keys());
  }

  /**
   * Obtener lista de configuraciones registradas
   */
  getRegisteredConfigs(): string[] {
    return Array.from(this.configCache.keys());
  }
}
